import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import * as styles from '../productPage/tableComponents/ProductPage_style';
import { Box, Divider, Drawer, IconButton } from '@mui/material';
import LibraryDrawer from './LibraryDrawer';
import ResourcesList from '../common/resourceLibrary/ResourcesList';
import ResultHeader from './ResourcesResultHeader';
import SearchBar from './SearchBar';
import { useResourceLibraryItems } from '../../graphql/pimcore/modules/resourceLibrary/contexts/ResourceLibraryItemsProvider';
import ReusableTabs from '../common/ReusableTabs';
import { useResponsive } from '../../contexts/ResponsiveContext';
import FilterControllerIcon from '../productPage/filterComponents/FilterControllerIcon';
import { useSalesAndResourcesResourceLibraryContent } from '../../graphql/pimcore/modules/siteContent/hooks/useSalesAndResourcesResourceLibraryContent';
import DebouncedLoader from '../common/DebouncedLoader';
import BreadCrumbs from '../common/BreadCrumbs';

const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => func(...args), delay);
    };
};

const ResourcesHome: FunctionComponent = () => {
    const { filteredItems, sortOrder, setSortOrder, setFilter, resetFilters } =
        useResourceLibraryItems();
    const [view, setView] = React.useState('module');

    const { isScreenSm, isScreenXs, isScreenMd } = useResponsive();
    const isSmallScreen = isScreenMd || isScreenSm || isScreenXs;
    const [searchTerm, setSearchTerm] = useState('');

    const [openDrawer, setOpenDrawer] = useState(false);

    const { loading, error, contentTree } = useSalesAndResourcesResourceLibraryContent();

    const debouncedHandleSearchTermFilterChange = useMemo(
        () =>
            debounce((value: string) => {
                setFilter('searchTerm', value || null);
            }, 300),
        [setFilter]
    );

    const handleInputChange = useCallback(
        (value: string) => {
            setSearchTerm(value); // Update the input display immediately
            debouncedHandleSearchTermFilterChange(value); // Update the searchTerm filter after debounce
        },
        [debouncedHandleSearchTermFilterChange]
    );

    const resetSearch = useCallback(() => {
        setSearchTerm('');
        setFilter('searchTerm', null);
    }, [setFilter]);

    const MemoizedLibraryDrawer = useMemo(
        () => <LibraryDrawer openDrawer={openDrawer} setOpenDrawer={setOpenDrawer} />,
        [openDrawer, setOpenDrawer]
    );
    const MemoizedResourcesList = useMemo(() => <ResourcesList view={view} />, [view]);
    const MemoizedResultHeader = useMemo(
        () => (
            <ResultHeader
                isDataSheet={false}
                items={filteredItems}
                sort={sortOrder}
                setSort={setSortOrder}
                onReset={resetSearch}
                view={view}
                setView={setView}
            />
        ),
        [filteredItems, sortOrder, setSortOrder, resetSearch, view, setView]
    );

    useEffect(() => {
        resetFilters();
    }, [resetFilters]);

    if (error && !loading) {
        return <div className="mid-width">Error: {error.message}</div>;
    }

    return (
        <DebouncedLoader loading={loading} delay={1000} size={40} centered={true}>
            <div>
                <ReusableTabs
                    isFullWidth
                    backgroundColor="#ffffff"
                    bannerImage={contentTree.pageTitleBackgroundImage.imageUrl}
                    bannerTitle={contentTree.pageTitle.content}
                    bannerDescription={
                        <SearchBar inputValue={searchTerm} onInputChange={handleInputChange} />
                    }
                    tabLabels={[]}
                    tabKeys={['']}
                    tabContents={[
                        <Box
                            key="resources-content"
                            sx={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                margin: 'auto',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                }}
                            >
                                {isSmallScreen && (
                                    <IconButton
                                        onClick={() => setOpenDrawer(true)}
                                        sx={{ marginLeft: '1rem' }}
                                    >
                                        <FilterControllerIcon />
                                    </IconButton>
                                )}
                                {MemoizedResultHeader}
                            </Box>
                            <Divider
                                orientation="horizontal"
                                sx={{ width: '100%', border: '0.1px solid #E5673E' }}
                            />

                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                }}
                            >
                                <Box
                                    sx={{
                                        width: isSmallScreen ? 0 : '22%',
                                    }}
                                >
                                    {MemoizedLibraryDrawer}
                                </Box>

                                <Box
                                    sx={{
                                        width: isSmallScreen ? '100%' : '80%',
                                    }}
                                >
                                    {MemoizedResourcesList}
                                </Box>
                            </Box>
                        </Box>,
                    ]}
                    bannerFontStyle={styles.bannerFont}
                    subBannerFontStyle={styles.bannerFont2}
                    pageName={'browseAllProductHome'}
                    showTabs={false}
                    breadcrumbs={
                        <BreadCrumbs navigationPath="/Home/SalesAndResources/ResourceLibrary" />
                    }
                />

                {isSmallScreen && (
                    <Drawer
                        anchor="left"
                        variant="temporary"
                        open={openDrawer}
                        onClose={() => setOpenDrawer(false)}
                        sx={{
                            '& .MuiDrawer-paper': {
                                width: '90%',
                                maxWidth: '476px',
                            },
                        }}
                    >
                        {MemoizedLibraryDrawer}
                    </Drawer>
                )}
            </div>
        </DebouncedLoader>
    );
};

export default ResourcesHome;
